import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

export const describeOpponentRange = (min, max) => {
  if (min === null || min === undefined || max === null || max === undefined) return '';
  const plural = (n) => (n === 1 ? 'time' : 'times');
  return min === max
    ? `Teams play each opponent ${min} ${plural(min)}`
    : `Teams play each opponent ${min}–${max} times`;
};

const OpponentMatrix = ({ result }) => {
  const theme = useTheme();
  const teams = useMemo(
    () => (result?.teamStats || []).map((t) => ({ id: t.teamId, name: t.teamName })),
    [result],
  );
  const meetings = useMemo(() => {
    const map = new Map();
    (result?.opponentCounts || []).forEach((c) => map.set(`${c.teamId}-${c.opponentTeamId}`, c.meetings));
    return map;
  }, [result]);

  if (teams.length === 0) {
    return <Typography color="text.secondary">No opponent data available.</Typography>;
  }

  const min = result?.minOpponentMeetings ?? 0;
  const max = result?.maxOpponentMeetings ?? 0;
  const range = Math.max(max - min, 1);

  const shade = (count) => {
    if (max === 0) return undefined;
    const intensity = 0.08 + 0.5 * ((count - min) / range);
    return alpha(theme.palette.primary.main, Math.max(0.04, Math.min(intensity, 0.6)));
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {describeOpponentRange(min, max)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Each cell shows how many times the row team meets the column team. Darker cells mean more meetings.
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small" aria-label="Opponent meetings">
          <TableHead>
            <TableRow>
              <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>Team</TableCell>
              {teams.map((t) => (
                <TableCell key={t.id} align="center" sx={{ minWidth: 56 }}>
                  <Box
                    component="span"
                    title={t.name}
                    sx={{ display: 'inline-block', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {t.name}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.map((row) => (
              <TableRow key={row.id}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, whiteSpace: 'nowrap' }}
                >
                  {row.name}
                </TableCell>
                {teams.map((col) => {
                  if (row.id === col.id) {
                    return <TableCell key={col.id} align="center" sx={{ bgcolor: 'grey.100' }}>—</TableCell>;
                  }
                  const count = meetings.get(`${row.id}-${col.id}`) ?? 0;
                  return (
                    <TableCell
                      key={col.id}
                      align="center"
                      data-testid={`meetings-${row.id}-${col.id}`}
                      sx={{ bgcolor: shade(count), fontWeight: count === max && max !== min ? 700 : 400 }}
                    >
                      {count}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default OpponentMatrix;
